import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";

function Shell({ icon: Icon, tone, title, copy, testId, children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bone px-4 pt-[72px]" data-testid={testId}>
      <div className="w-full max-w-md rounded-[2rem] border border-border bg-white p-10 text-center shadow-[0_25px_70px_rgba(10,41,30,0.08)]">
        <span className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${tone}`}>
          <Icon className={`h-6 w-6 ${Icon === Loader2 ? "animate-spin" : ""}`} />
        </span>
        <h1 className="mt-5 font-display text-2xl font-extrabold text-forest-deep">{title}</h1>
        <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{copy}</p>
        {children}
        <Link to="/" data-testid="payment-home-btn"
          className="mt-6 inline-block rounded-full bg-forest px-8 py-3.5 font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5">
          Back to home
        </Link>
      </div>
    </div>
  );
}

function usePaymentStatus(sessionId) {
  const [state, setState] = useState({ status: sessionId ? "checking" : "error" });
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState({ status: "paid", data });
        if (data.status === "expired") return setState({ status: "expired" });
      } catch {
        return setState({ status: "error" });
      }
      if (++attempts >= 10) return setState({ status: "timeout" });
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);
  return state;
}

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const { status, data } = usePaymentStatus(params.get("session_id"));
  if (status === "checking")
    return <Shell icon={Loader2} tone="bg-gold/15 text-gold-dark" testId="payment-checking"
      title="Confirming your payment" copy="This only takes a moment…" />;
  if (status === "paid")
    return (
      <Shell icon={CheckCircle2} tone="bg-emerald-50 text-emerald-600" testId="payment-success"
        title="Registration confirmed" copy="Your payment was successful and a confirmation email is on its way.">
        <p className="mt-4 rounded-2xl bg-secondary/60 p-4 text-sm text-forest-deep" data-testid="payment-success-details">
          <strong>{data.course_title}</strong><br />Preferred start: {data.start_date}
        </p>
      </Shell>
    );
  return <Shell icon={XCircle} tone="bg-red-50 text-red-500" testId="payment-failed"
    title={status === "expired" ? "Payment session expired" : "We couldn't confirm your payment"}
    copy="If you were charged, our team will reach out. Otherwise, please try registering again." />;
}

export function PaymentCancelPage() {
  return <Shell icon={XCircle} tone="bg-red-50 text-red-500" testId="payment-cancelled"
    title="Payment cancelled" copy="No payment was taken. You can register again anytime from the course page." />;
}
